import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Plus, Wrench, Phone, Envelope, Translate, PencilSimple, ArrowCounterClockwise, PaperPlaneTilt, SpinnerGap, X } from "phosphor-react";
import ConfirmModal from "../components/ConfirmModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Technicians() {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTechId, setEditingTechId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_language: "en",
    specializations: [],
  });
  
  // Owner as technician setting
  const [allowOwnerAsTechnician, setAllowOwnerAsTechnician] = useState(false);
  const [ownerTechnicianInfo, setOwnerTechnicianInfo] = useState(null);
  const [savingOwnerSetting, setSavingOwnerSetting] = useState(false);
  
  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ open: false, techId: null });

  useEffect(() => {
    fetchTechnicians();
    fetchOwnerTechnicianSetting();
  }, []);
  
  const fetchOwnerTechnicianSetting = async () => {
    try {
      const response = await fetch(`${API}/tenants/owner-technician-setting`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setAllowOwnerAsTechnician(data.allow_owner_as_technician);
        setOwnerTechnicianInfo(data.owner_technician);
      }
    } catch (error) {
      console.error("Error fetching owner technician setting:", error);
    }
  };

  const toggleOwnerAsTechnician = async (enabled) => {
    setSavingOwnerSetting(true);
    try {
      const response = await fetch(`${API}/tenants/owner-technician-setting`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ allow_owner_as_technician: enabled }),
      });

      if (response.ok) {
        const data = await response.json();
        setAllowOwnerAsTechnician(data.allow_owner_as_technician);
        setOwnerTechnicianInfo(data.owner_technician);
        toast.success(
          enabled 
            ? "Owner will appear in technician assignments" 
            : "Owner removed from technician assignments"
        );
        fetchTechnicians(); // Refresh list
      } else {
        toast.error("Failed to update setting");
      }
    } catch (error) {
      toast.error("Failed to update setting");
    } finally {
      setSavingOwnerSetting(false);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await fetch(`${API}/technicians`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setTechnicians(data.technicians);
      }
    } catch (error) {
      console.error("Error fetching technicians:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      preferred_language: "en",
      specializations: [],
    });
  };

  const handleOpenDialog = (tech = null) => {
    if (tech) {
      setIsEditMode(true);
      setEditingTechId(tech.technician_id);
      setFormData({
        name: tech.name || "",
        email: tech.email || "",
        phone: tech.phone || "",
        preferred_language: tech.preferred_language || "en",
        specializations: tech.specializations || [],
      });
    } else {
      setIsEditMode(false);
      setEditingTechId(null);
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setIsEditMode(false);
    setEditingTechId(null);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error("Email is required");
      return;
    }
    
    const url = isEditMode ? `${API}/technicians/${editingTechId}` : `${API}/technicians`;
    const method = isEditMode ? "PUT" : "POST";

    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      toast.success(isEditMode ? "Technician updated" : "Technician added");
      handleCloseDialog();
      fetchTechnicians();
    } else {
      const error = await response.json();
      toast.error(error.detail || `Failed to ${isEditMode ? "update" : "add"} technician`);
    }
  };

  const deactivateTechnician = async (technicianId) => {
    try {
      const response = await fetch(`${API}/technicians/${technicianId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Technician deactivated");
        fetchTechnicians();
      }
    } catch (error) {
      toast.error("Failed to deactivate technician");
    }
  };
  
  const handleDeactivateClick = (technicianId) => {
    setConfirmModal({ open: true, techId: technicianId });
  };
  
  const handleConfirmDeactivate = () => {
    if (confirmModal.techId) {
      deactivateTechnician(confirmModal.techId);
    }
    setConfirmModal({ open: false, techId: null });
  };

  const reactivateTechnician = async (technicianId) => {
    try {
      const response = await fetch(`${API}/technicians/${technicianId}/reactivate`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Technician reactivated");
        fetchTechnicians();
      }
    } catch (error) {
      toast.error("Failed to reactivate technician");
    }
  };

  // Send login email - unified action for all cases
  const sendLoginEmail = async (technicianId) => {
    setActionLoading(technicianId);
    try {
      const response = await fetch(`${API}/technicians/${technicianId}/send-login-email`, {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Login email sent!");
      } else {
        toast.error(data.detail || "Failed to send login email");
      }
    } catch (error) {
      toast.error("Failed to send login email");
    } finally {
      setActionLoading(null);
    }
  };

  // Check if technician has set up their login
  const hasLogin = (tech) => {
    return tech.login_status === "active";
  };

  const activeTechnicians = technicians.filter(t => t.is_active !== false);
  const inactiveTechnicians = technicians.filter(t => t.is_active === false);

  return (
    <div className="space-y-6" data-testid="technicians-page">
      {/* Owner as Technician Setting - Moved from Company Information */}
      <Card className="border-2 border-zinc-200 bg-zinc-50">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <Label className="text-base font-semibold">
                Allow Owner as Technician
              </Label>
              <p className="text-sm text-zinc-500 mt-1">
                If enabled, the owner will appear in technician assignment lists for scheduling visits.
                Ideal for sole practitioners who work on jobs themselves.
              </p>
              {ownerTechnicianInfo && allowOwnerAsTechnician && (
                <p className="text-xs text-emerald-600 mt-2">
                  ✓ Owner technician profile: {ownerTechnicianInfo.name}
                </p>
              )}
            </div>
            <Switch
              checked={allowOwnerAsTechnician}
              onCheckedChange={toggleOwnerAsTechnician}
              disabled={savingOwnerSetting}
              data-testid="owner-as-technician-toggle"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold uppercase">Technicians</h2>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) handleCloseDialog(); else if (!isEditMode) handleOpenDialog(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm" data-testid="add-technician-btn">
              <Plus size={18} className="mr-2" />
              Add Technician
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase">{isEditMode ? "Edit Technician" : "Add Technician"}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <form id="technician-form" onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Email *</Label>
                  <Input 
                    type="email" 
                    value={formData.email} 
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                    placeholder="technician@example.com" 
                    className="mt-1 h-11 border-2" 
                    required 
                    data-testid="technician-email-input" 
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    The technician will receive an email invitation to join your team.
                  </p>
                </div>
                <div>
                  <Label>Name</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                    placeholder="John Smith" 
                    className="mt-1 h-11 border-2" 
                    data-testid="technician-name-input" 
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input 
                    value={formData.phone} 
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                    placeholder="(555) 123-4567" 
                    className="mt-1 h-11 border-2" 
                    data-testid="technician-phone-input" 
                  />
                </div>
                <div>
                  <Label>Preferred Language</Label>
                  <Select value={formData.preferred_language} onValueChange={(v) => setFormData({ ...formData, preferred_language: v })}>
                    <SelectTrigger className="mt-1 h-11 border-2" data-testid="technician-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </form>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="flex-1 sm:flex-none border-2">Cancel</Button>
              <Button type="submit" form="technician-form" className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white" data-testid="save-technician-btn">{isEditMode ? "Save Changes" : "Add Technician"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (<div key={i} className="h-40 bg-zinc-200 rounded-sm animate-pulse" />))}
        </div>
      ) : technicians.length > 0 ? (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeTechnicians.map((tech) => (
              <Card key={tech.technician_id} className="border-2 border-zinc-200 card-hover">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-[#FF6600]/10 border-2 border-[#FF6600]/30 flex items-center justify-center flex-shrink-0">
                      <Wrench size={28} className="text-[#FF6600]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{tech.name || tech.email?.split("@")[0] || "Unnamed"}</p>
                        {tech.is_owner && (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Owner</Badge>
                        )}
                        {tech.preferred_language === "es" && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                            <Translate size={12} className="mr-1" />ES
                          </Badge>
                        )}
                      </div>
                      <div className="mt-2 space-y-1 text-sm text-zinc-500">
                        {tech.email && (
                          <p className="flex items-center gap-2 truncate">
                            <Envelope size={14} />{tech.email}
                          </p>
                        )}
                        {tech.phone && (
                          <p className="flex items-center gap-2">
                            <Phone size={14} />{tech.phone}
                          </p>
                        )}
                      </div>
                      {tech.specializations?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {tech.specializations.map((spec, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">{spec}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons - Always Visible, No Hidden Menus */}
                  {/* Owner technician card is not editable - only show actions for non-owner techs */}
                  <div className="flex flex-wrap justify-end mt-4 gap-2">
                    {!tech.is_owner && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
                        onClick={() => handleOpenDialog(tech)}
                        data-testid={`edit-btn-${tech.technician_id}`}
                      >
                        <PencilSimple size={16} className="mr-1" />
                        Edit
                      </Button>
                    )}
                    
                    {tech.email && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => sendLoginEmail(tech.technician_id)}
                        disabled={actionLoading === tech.technician_id}
                        data-testid={`send-login-btn-${tech.technician_id}`}
                      >
                        {actionLoading === tech.technician_id ? (
                          <SpinnerGap size={16} className="animate-spin mr-1" />
                        ) : (
                          <PaperPlaneTilt size={16} className="mr-1" />
                        )}
                        Send Login Email
                      </Button>
                    )}
                    
                    {!tech.is_owner && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeactivateClick(tech.technician_id)}
                        data-testid={`deactivate-btn-${tech.technician_id}`}
                      >
                        <X size={16} className="mr-1" />
                        Deactivate
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {inactiveTechnicians.length > 0 && (
            <div className="mt-8">
              <h3 className="font-heading text-lg font-semibold uppercase mb-4 text-zinc-500">Inactive Technicians</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {inactiveTechnicians.map((tech) => (
                  <Card key={tech.technician_id} className="border-2 border-zinc-200 bg-zinc-50 opacity-75">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-zinc-200 border-2 border-zinc-300 flex items-center justify-center flex-shrink-0">
                          <Wrench size={28} className="text-zinc-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium truncate text-zinc-500">{tech.name || tech.email?.split("@")[0] || "Unnamed"}</p>
                            <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs">Inactive</Badge>
                          </div>
                          <div className="mt-2 space-y-1 text-sm text-zinc-400">
                            {tech.email && (
                              <p className="flex items-center gap-2 truncate">
                                <Envelope size={14} />{tech.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-300"
                          onClick={() => reactivateTechnician(tech.technician_id)}
                        >
                          <ArrowCounterClockwise size={16} className="mr-1" />
                          Reactivate
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="p-12 text-center">
            <Wrench size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg font-semibold uppercase mb-2">No Technicians Yet</h3>
            <p className="text-zinc-500 mb-6">Add your team members to assign them to jobs</p>
            <Button onClick={() => setIsDialogOpen(true)} className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm">
              <Plus size={18} className="mr-2" />Add Technician
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Deactivate Confirmation Modal */}
      <ConfirmModal
        open={confirmModal.open}
        onOpenChange={(open) => setConfirmModal({ ...confirmModal, open })}
        title="Deactivate Technician"
        message="Are you sure you want to deactivate this technician? They will no longer be assignable to new jobs."
        confirmText="Deactivate"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setConfirmModal({ open: false, techId: null })}
      />
    </div>
  );
}
